import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type {
  AdminRole,
  Money,
  Settlement,
} from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import {
  canExecutePayout,
  nextApprovalStage,
  type ApprovalStage,
} from '../lib/settlementActionRbac';
import { useSettlementQuery } from '../hooks/useSettlementQuery';
import { useSettlementItemsQuery } from '../hooks/useSettlementItemsQuery';
import { usePayoutsQuery } from '../hooks/usePayoutsQuery';
import {
  useApproveSettlementMutation,
  useCreatePayoutMutation,
} from '../hooks/useSettlementMutation';
import { SettlementRunStatusPill } from '../components/SettlementRunStatusPill';
import { ApproveModal } from '../components/ApproveModal';
import { PayoutCreateModal } from '../components/PayoutCreateModal';
import { SettlementItemsTable } from '../components/SettlementItemsTable';
import { PayoutTable } from '../components/PayoutTable';
import { AuditTrailPanel } from '../components/AuditTrailPanel';

type Tab = 'overview' | 'items' | 'payout' | 'audit';

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) {
    if (err.code === 'SETTLEMENT_APPROVAL_2FA_REQUIRED')
      return 'settlement.errors.twoFaRequired';
    if (err.code === 'SETTLEMENT_POLICY_APPROVAL_SELF')
      return 'settlement.errors.approvalSelf';
    if (err.code === 'SETTLEMENT_PAYOUT_UNAUTHORIZED')
      return 'settlement.errors.payoutPermissionDenied';
    if (err.code === 'SETTLEMENT_PAYOUT_MANUAL_REQUIRED')
      return 'settlement.errors.payoutMinimum';
    return err.message ?? err.code;
  }
  if (err instanceof Error) return err.message;
  return null;
};

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
};

const OverviewSection = ({ settlement }: { settlement: Settlement }) => {
  const { t } = useTranslation('settlement');
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <section
        className="md:col-span-2 rounded-lg p-5"
        style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
        aria-labelledby="settlement-aggregate"
      >
        <h3
          id="settlement-aggregate"
          className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('settlement.detail.sectionAggregate')}
        </h3>
        <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
          <Field label={t('settlement.fields.grossAmount')} value={formatMoney(settlement.grossAmount)} />
          <Field label={t('settlement.fields.returnAmount')} value={formatMoney(settlement.returnAmount)} />
          <Field
            label={t('settlement.fields.cancellationAmount')}
            value={formatMoney(settlement.cancellationAmount)}
          />
          <Field
            label={t('settlement.fields.commissionAmount')}
            value={formatMoney(settlement.commissionAmount)}
            tone="warn"
          />
          <Field
            label={t('settlement.fields.netPayout')}
            value={formatMoney(settlement.netPayout)}
            tone="strong"
          />
          <Field
            label={t('settlement.fields.itemCount')}
            value={String(settlement.itemCount ?? 0)}
          />
        </dl>
      </section>

      <section
        className="rounded-lg p-5"
        style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
        aria-labelledby="settlement-meta"
      >
        <h3
          id="settlement-meta"
          className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('settlement.detail.sectionMeta')}
        </h3>
        <dl className="flex flex-col gap-2 text-sm">
          <Field label={t('settlement.fields.runId')} value={settlement.runId} mono />
          <Field label={t('settlement.fields.cycle')} value={t(`settlement.cycle.${settlement.cycle}`, { defaultValue: '-' })} />
          <Field
            label={t('settlement.fields.runType')}
            value={t(`settlement.runType.${settlement.runType}`, { defaultValue: '-' })}
          />
          <Field
            label={t('settlement.fields.computedAt')}
            value={formatTime(settlement.computedAt)}
          />
          <Field
            label={t('settlement.fields.paidOutAt')}
            value={formatTime(settlement.paidOutAt)}
          />
        </dl>
      </section>

      <section
        className="md:col-span-3 rounded-lg p-5"
        style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
        aria-labelledby="settlement-approvals"
      >
        <h3
          id="settlement-approvals"
          className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
        >
          {t('settlement.detail.approvalFlow')}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ApprovalSlot
            label={t('settlement.detail.approval1st')}
            admin={settlement.approvedByAgentId}
          />
          <ApprovalSlot
            label={t('settlement.detail.approval2nd')}
            admin={settlement.approvedBySuperAdminId}
          />
        </div>
      </section>
    </div>
  );
};

const ApprovalSlot = ({
  label,
  admin,
}: {
  label: string;
  admin: string | null | undefined;
}) => {
  const { t } = useTranslation('settlement');
  return (
    <div
      className="rounded-md p-3"
      style={{ background: 'var(--surface-1)' }}
      data-testid="approval-slot"
    >
      <span className="block text-xs text-[color:var(--content-muted)]">{label}</span>
      <span className="block mt-1 text-sm text-[color:var(--content-strong)]">
        {admin ? t('settlement.detail.approvedBy', { admin }) : t('settlement.detail.notApproved')}
      </span>
    </div>
  );
};

const Field = ({
  label,
  value,
  tone,
  mono,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'strong';
  mono?: boolean;
}) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-[color:var(--content-muted)]">{label}</dt>
    <dd
      className={`text-sm ${mono ? 'font-mono' : ''}`}
      style={{
        color:
          tone === 'strong'
            ? 'var(--content-strong)'
            : tone === 'warn'
              ? 'var(--status-warn-fg)'
              : 'var(--content-default)',
        fontWeight: tone === 'strong' ? 600 : 400,
      }}
    >
      {value}
    </dd>
  </div>
);

export const SettlementDetailPage = () => {
  const { t } = useTranslation('settlement');
  const params = useParams<{ runId: string }>();
  const runId = params.runId ?? '';

  const { currentAdmin } = useAuthContext();
  const roles = (currentAdmin?.roles ?? []) as ReadonlyArray<AdminRole>;
  const currentAdminId = currentAdmin?.adminUserId ?? null;

  const query = useSettlementQuery(runId);
  const itemsQuery = useSettlementItemsQuery({
    runId,
    page: 1,
    size: 50,
    sort: 'createdAt,desc',
  });
  const payoutsQuery = usePayoutsQuery(runId);
  const approveMutation = useApproveSettlementMutation();
  const payoutMutation = useCreatePayoutMutation();

  const [tab, setTab] = useState<Tab>('overview');
  const [approveOpen, setApproveOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);

  const settlement = query.data;
  const items = itemsQuery.data?.items ?? [];
  const payouts = payoutsQuery.data ?? [];
  const approveError = errorMessageOf(approveMutation.error);
  const payoutError = errorMessageOf(payoutMutation.error);

  const tabs = useMemo<Array<{ id: Tab; label: string }>>(
    () => [
      { id: 'overview', label: t('settlement.detail.tabOverview') },
      {
        id: 'items',
        label: t('settlement.detail.tabItems', { count: settlement?.itemCount ?? items.length }),
      },
      { id: 'payout', label: t('settlement.detail.tabPayout') },
      { id: 'audit', label: t('settlement.detail.tabAudit') },
    ],
    [t, settlement, items],
  );

  if (query.isPending) {
    return (
      <section className="py-12 text-center text-sm text-[color:var(--content-muted)]">
        {t('settlement.detail.loading')}
      </section>
    );
  }
  if (query.isError || !settlement) {
    return (
      <section
        role="alert"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
      >
        <p>{t('settlement.detail.error')}</p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          {t('settlement.actions.retry')}
        </Button>
      </section>
    );
  }

  const approval = nextApprovalStage(settlement, currentAdminId, roles);
  const stage: ApprovalStage = approval.stage;
  const payoutAvailable = canExecutePayout(settlement, roles);

  const detailHeaderTitle = `${settlement.vendorName ?? settlement.vendorId} · ${t(`settlement.cycle.${settlement.cycle}`)} ${settlement.periodStart}`;

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {detailHeaderTitle}
          <SettlementRunStatusPill
            status={settlement.status}
            runId={settlement.runId}
          />
          <span className="block text-xs font-normal text-[color:var(--content-muted)]">
            {t('settlement.detail.subject', {
              vendorName: settlement.vendorName ?? settlement.vendorId,
              cycle: t(`settlement.cycle.${settlement.cycle}`),
              periodStart: settlement.periodStart,
              periodEnd: settlement.periodEnd,
            })}
          </span>
        </span>
      }
      breadcrumb={[
        { label: t('settlement.list.title'), href: '/settlements' },
        { label: detailHeaderTitle },
      ]}
      actions={
        <div className="flex items-center gap-2">
          {stage !== 'NONE' ? (
            <Button
              type="button"
              variant="cta"
              size="sm"
              onClick={() => setApproveOpen(true)}
              data-testid="approve-trigger"
            >
              {stage === '1ST'
                ? t('settlement.actions.approveAgent')
                : t('settlement.actions.approveSuper')}
            </Button>
          ) : null}
          {payoutAvailable ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setPayoutOpen(true)}
              data-testid="payout-trigger"
            >
              {t('settlement.actions.payout')}
            </Button>
          ) : null}
        </div>
      }
    >

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          {tabs.map((tabItem) => (
            <TabsTrigger
              key={tabItem.id}
              value={tabItem.id}
              data-testid={`tab-${tabItem.id}`}
            >
              {tabItem.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <OverviewSection settlement={settlement} />
        </TabsContent>

        <TabsContent value="items">
          {itemsQuery.isPending ? (
            <p className="text-sm text-[color:var(--content-muted)]">
              {t('settlement.items.loading')}
            </p>
          ) : itemsQuery.isError ? (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {t('settlement.items.error')}
            </p>
          ) : (
            <SettlementItemsTable items={items} />
          )}
        </TabsContent>

        <TabsContent value="payout">
          {payoutsQuery.isPending ? (
            <p className="text-sm text-[color:var(--content-muted)]">
              {t('settlement.payouts.loading')}
            </p>
          ) : payoutsQuery.isError ? (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {t('settlement.payouts.error')}
            </p>
          ) : (
            <PayoutTable items={payouts} />
          )}
        </TabsContent>

        <TabsContent value="audit">
          <AuditTrailPanel settlement={settlement} payouts={payouts} />
        </TabsContent>
      </Tabs>

      {stage !== 'NONE' ? (
        <ApproveModal
          isOpen={approveOpen}
          stage={stage}
          runId={settlement.runId}
          vendor={settlement.vendorName ?? settlement.vendorId}
          isSubmitting={approveMutation.isPending}
          blockedBySelfRule={approval.blockedBySelfRule}
          errorMessage={
            approveError
              ? t(approveError, { defaultValue: approveError })
              : null
          }
          onCancel={() => setApproveOpen(false)}
          onConfirm={(values) =>
            approveMutation.mutate(
              {
                runId: settlement.runId,
                body: values.note ? { note: values.note } : undefined,
              },
              { onSuccess: () => setApproveOpen(false) },
            )
          }
        />
      ) : null}

      <PayoutCreateModal
        isOpen={payoutOpen}
        runId={settlement.runId}
        vendor={settlement.vendorName ?? settlement.vendorId}
        netPayout={settlement.netPayout}
        canExecute={payoutAvailable}
        isSubmitting={payoutMutation.isPending}
        errorMessage={
          payoutError ? t(payoutError, { defaultValue: payoutError }) : null
        }
        onCancel={() => setPayoutOpen(false)}
        onConfirm={(input) => {
          const body: { bankAccountId?: string; note?: string | null } = {};
          if (input.bankAccountId) body.bankAccountId = input.bankAccountId;
          if (input.note) body.note = input.note;
          payoutMutation.mutate(
            { runId: settlement.runId, body: Object.keys(body).length > 0 ? body : undefined },
            { onSuccess: () => setPayoutOpen(false) },
          );
        }}
      />
    </PageShell>
  );
};
